import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React from 'react';
import {
  BooleanField,
  EditButton,
  FunctionField,
  ImageField,
  NumberField,
  Show,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import type { AdminExerciseDetailsFragment } from '../../../graphql/types';
import { ResourceLink } from '../../reusable/ResourceLink';
import ClipboardField from '../components/ClipboardField';
import CopyIconButton from '../components/CopyIconButton';
import { ArchiveResourceButton } from './CategoryActions';

/** ISO NIST; oneRepMax values from the API are stored in kilograms. */
const POUNDS_PER_KILOGRAM = 2.2046226218;

function formatKilogramsForDisplay(kg: number): string {
  return String(Number(kg.toFixed(2)));
}

const ExerciseShowActions: React.FC = () => {
  const record = useRecordContext<AdminExerciseDetailsFragment>();

  return (
    <TopToolbar>
      <EditButton disabled={!!record?.isArchived} />
      <ArchiveResourceButton resource="Exercise" />
    </TopToolbar>
  );
};

const ExerciseShowView: React.FC = () => {
  return (
    <SimpleShowLayout>
      <ClipboardField source="id" fontSize={14} />
      <TextField source="name" label="Name" />
      <TextField source="displayName" />
      <FunctionField
        label="MTC Group"
        render={(exerciseRecord: AdminExerciseDetailsFragment) => {
          const group =
            exerciseRecord.exerciseBase?.exerciseType?.exerciseTypeCategory
              ?.exerciseTypeCategoryGroup;
          if (!group?.name) {
            return (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                —
              </Box>
            );
          }
          return (
            <ResourceLink
              name="ExerciseTypeCategoryGroup"
              type="show"
              id={group.id}
            >
              {group.name}
            </ResourceLink>
          );
        }}
      />
      <FunctionField
        label="MTC"
        render={(exerciseRecord: AdminExerciseDetailsFragment) => {
          const cat =
            exerciseRecord.exerciseBase?.exerciseType?.exerciseTypeCategory;
          if (!cat?.name) {
            return (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                —
              </Box>
            );
          }
          return (
            <ResourceLink name="ExerciseTypeCategory" type="show" id={cat.id}>
              {cat.name}
            </ResourceLink>
          );
        }}
      />
      <FunctionField
        label="Movement Type"
        render={(exerciseRecord: AdminExerciseDetailsFragment) => {
          const et = exerciseRecord.exerciseBase?.exerciseType;
          if (!et?.name) {
            return (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                —
              </Box>
            );
          }
          return (
            <ResourceLink name="ExerciseType" type="show" id={et.id}>
              {et.name}
            </ResourceLink>
          );
        }}
      />
      <FunctionField
        label="Base Movement"
        render={(exerciseRecord: AdminExerciseDetailsFragment) =>
          exerciseRecord.exerciseBase ? (
            <ResourceLink
              name="ExerciseBase"
              type="show"
              id={exerciseRecord.exerciseBase.id}
            >
              {exerciseRecord.exerciseBase.name}
            </ResourceLink>
          ) : (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              —
            </Box>
          )
        }
      />
      <FunctionField
        key="coachingKeys"
        label="Instructions"
        render={(exerciseRecord: { coachingKeys?: string[] }) => (
          <ul>
            {exerciseRecord.coachingKeys?.map((key: string) => (
              <li key={key}>{key}</li>
            ))}
          </ul>
        )}
      />
      <TextField
        source="modifications"
        label="Modifications"
        style={{ whiteSpace: 'pre' }}
      />
      <TextField
        source="coachNotes"
        label="Coach Notes"
        style={{ whiteSpace: 'pre' }}
      />
      <TextField source="defaultUnit.name" label="Default Unit" />
      <BooleanField source="canTrackWeight" label="Can Track Weight" />
      <NumberField source="progression" label="Progression" />

      <FunctionField
        label="Body parts"
        render={(record: { bodyParts?: string[] }) => (
          <ul>
            {record.bodyParts?.map((part) => (
              <li key={part}>{part}</li>
            ))}
          </ul>
        )}
      />
      <FunctionField
        label="Equipment"
        render={(record: { equipment?: string[] }) => (
          <ul>
            {record.equipment?.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}
      />
      <FunctionField
        label="One rep max"
        render={(record: { oneRepMax?: number[] }) => {
          const rows = record.oneRepMax ?? [];
          if (rows.length === 0) {
            return (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                —
              </Box>
            );
          }
          return (
            <TableContainer
              component={Box}
              sx={{ maxWidth: 200, border: 1, borderColor: 'divider' }}
            >
              <Table size="small" aria-label="One rep max weights">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        borderRight: 1,
                        borderColor: 'divider',
                      }}
                    >
                      KG
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>LBS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((kg, index) => (
                    <TableRow
                      // biome-ignore lint/suspicious/noArrayIndexKey: it's fine
                      key={index}
                    >
                      <TableCell
                        sx={{ borderRight: 1, borderColor: 'divider' }}
                      >
                        {formatKilogramsForDisplay(kg)}
                      </TableCell>
                      <TableCell>
                        {Math.round(kg * POUNDS_PER_KILOGRAM)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      />
      <TextField source="apmlId" label="APML ID" />
      <FunctionField
        source="video"
        label="Video"
        render={(record: { video?: { url: string } | null }) => {
          if (!record.video) {
            return null;
          }
          return (
            <>
              {/* biome-ignore lint/a11y/useMediaCaption: No captions for exercise videos */}
              <video
                src={record.video.url}
                controls
                style={{ width: '100%', maxWidth: 480 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Link
                  href={record.video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {record.video.url}
                </Link>
                <CopyIconButton text={record.video.url} />
              </Box>
            </>
          );
        }}
      />
      <ImageField
        source="thumbnail.url"
        label="Thumbnail"
        sx={{
          '& img': {
            width: '100% !important',
            maxWidth: 480,
            height: 'auto !important',
            margin: '0 !important',
          },
        }}
      />
    </SimpleShowLayout>
  );
};

export const ExerciseShow: React.FC = () => (
  <Show actions={<ExerciseShowActions />}>
    <ExerciseShowView />
  </Show>
);
